import { useContext, useEffect, useState } from "react";
import { Card, CardContent } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/ui/collapsible";
import {
  Image as ImageIcon,
  Loader2,
  Package,
  Sparkles,
} from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { supabase } from "@/shared/integrations/supabase/client";
import { firebaseAuth } from "@/shared/integrations/firebase/client";
import { captureEdgeFailure } from "@/shared/integrations/sentry/reportEdgeFailure";
import { DraggablePhotoViewer } from "../DraggablePhotoViewer";
import { FirebaseImage } from "@/shared/components/FirebaseImage";
import { AssetsByCategoryContext } from "./contexts";

export function PropertyAssetsSection({ propertyId }: { propertyId: string }) {
  const assetsByCategory = useContext(AssetsByCategoryContext);
  const categories = Object.entries(assetsByCategory);
  const isAiAdmin = firebaseAuth?.currentUser?.email === "mario@roboticimaging.com";
  const [viewer, setViewer] = useState<{
    photos: Array<{ url: string; name: string }>;
    index: number;
    title: string;
  } | null>(null);

  // AI processing trigger state
  const [aiStatus, setAiStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [aiResult, setAiResult] = useState<{
    queued?: number;
    qa_seeded?: number;
    already_processed?: number;
    skipped_no_photos?: number;
    total?: number;
    error?: string;
  } | null>(null);

  // Auto-dismiss success message after 8 seconds
  useEffect(() => {
    if (aiStatus === 'success') {
      const timer = setTimeout(() => {
        setAiStatus('idle');
        setAiResult(null);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [aiStatus]);

  const handleTriggerAI = async (e: React.MouseEvent) => {
    // Prevent the collapsible from toggling
    e.stopPropagation();
    if (aiStatus === 'loading') return;

    setAiStatus('loading');
    setAiResult(null);

    try {
      const { data, error } = await supabase.functions.invoke(
        'trigger-asset-processing',
        { body: { property_id: propertyId } }
      );

      if (error) {
        captureEdgeFailure({ endpoint: 'trigger-asset-processing', error });
        setAiStatus('error');
        setAiResult({ error: error.message || 'Failed to trigger processing' });
        return;
      }

      setAiStatus('success');
      setAiResult(data);
    } catch (err) {
      captureEdgeFailure({ endpoint: 'trigger-asset-processing', error: err });
      setAiStatus('error');
      setAiResult({ error: (err as Error).message || 'Unexpected error' });
    }
  };

  if (categories.length === 0) return null;

  const totalAssets = categories.reduce(
    (sum, [, cat]) => sum + cat.assets.length,
    0
  );
  const totalPhotos = categories.reduce(
    (sum, [, cat]) => sum + cat.photos.filter((p) => p.signedUrl).length,
    0
  );

  return (
    <>
      <Collapsible defaultOpen={false}>
        <Card className="border border-border/50 overflow-hidden bg-card/50 backdrop-blur-sm">
          <div className="w-full flex items-center justify-between p-4">
            <CollapsibleTrigger asChild>
              <button className="flex items-center gap-2 flex-1 min-w-0 hover:bg-accent/30 transition-colors text-left rounded-md -m-1 p-1">
                <Package className="h-4 w-4 text-primary" />
                <span className="font-medium text-base text-foreground">
                  Property Assets
                </span>
              </button>
            </CollapsibleTrigger>
            <div className="flex items-center gap-2">
              {/* AI Processing button — admin only */}
              {isAiAdmin && (
                <button
                  onClick={handleTriggerAI}
                  disabled={aiStatus === 'loading'}
                  className={cn(
                    "flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors",
                    aiStatus === 'loading'
                      ? "bg-primary/10 text-primary cursor-wait"
                      : "bg-primary/10 text-primary hover:bg-primary/20"
                  )}
                >
                  {aiStatus === 'loading' ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Sparkles className="h-3 w-3" />
                  )}
                  {aiStatus === 'loading' ? "Processing..." : "Process with AI"}
                </button>
              )}
              {/* Result feedback */}
              {isAiAdmin && aiStatus === 'success' && aiResult && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                  ✓{aiResult.queued ? ` ${aiResult.queued} queued` : ''}{aiResult.qa_seeded ? ` ${aiResult.qa_seeded} QA items created` : ''}{!aiResult.queued && !aiResult.qa_seeded ? ` ${aiResult.already_processed || 0} already done` : ''}
                </span>
              )}
              {isAiAdmin && aiStatus === 'error' && aiResult?.error && (
                <span className="text-xs text-destructive font-medium max-w-[200px] truncate">
                  {aiResult.error}
                </span>
              )}
              <Badge variant="secondary" className="text-xs">
                {totalAssets} asset{totalAssets !== 1 ? "s" : ""}
              </Badge>
              {totalPhotos > 0 && (
                <Badge variant="outline" className="text-xs gap-1">
                  <ImageIcon className="h-3 w-3" />
                  {totalPhotos}
                </Badge>
              )}
            </div>
          </div>

          <CollapsibleContent>
            <CardContent className="pt-0 pb-3 px-4 space-y-0.5">
              {categories
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([categoryName, { assets, photos }]) => {
                  const signedPhotos = photos.filter((p) => p.signedUrl);
                  return (
                    <div key={categoryName}>
                      {/* Category header */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-foreground capitalize">
                          {categoryName}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {assets.length} asset{assets.length !== 1 ? "s" : ""}
                          {signedPhotos.length > 0 &&
                            ` · ${signedPhotos.length} photo${signedPhotos.length !== 1 ? "s" : ""}`}
                        </span>
                      </div>

                      {/* Photo thumbnails */}
                      {signedPhotos.length > 0 ? (
                        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
                          {signedPhotos.map((photo, idx) => (
                            <button
                              key={photo.id}
                              onClick={() =>
                                setViewer({
                                  photos: signedPhotos.map((p) => ({
                                    url: p.signedUrl!,
                                    name: p.document_name,
                                  })),
                                  index: idx,
                                  title: `${categoryName} Assets`,
                                })
                              }
                              className="relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border border-border/50 hover:border-primary/50 hover:ring-2 hover:ring-primary/20 transition-all group"
                            >
                              <FirebaseImage
                                src={photo.signedUrl}
                                alt={photo.document_name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                                loading="lazy"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                              <span className="absolute bottom-0.5 left-0.5 right-0.5 text-[9px] text-white truncate opacity-0 group-hover:opacity-100 transition-opacity">
                                {photo.document_name}
                              </span>
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 py-2 px-3 rounded-md bg-muted/30 border border-border/30 flex-wrap">
                          {assets.map((asset) => (
                            <span
                              key={asset.id}
                              className="text-xs text-muted-foreground"
                            >
                              {asset.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Draggable viewer */}
      {viewer && (
        <DraggablePhotoViewer
          photos={viewer.photos}
          initialIndex={viewer.index}
          title={viewer.title}
          onClose={() => setViewer(null)}
        />
      )}
    </>
  );
}
